import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Canceler } from "axios";
import { useFormContainerState, useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    Button,
    HStack,
    Hide,
    VStack,
    useToast,
    Text,
    Badge,
    Box,
    ButtonGroup,
} from "@chakra-ui/react";
import { AppSearch } from "../../../containers";
import {
    AppFilterButton,
    AppIcon,
    AppLoader,
    AppOptionBoxItem,
    AppOptionsBox,
    AppPageContent,
    AppPageHeader,
    AppPagination,
    AppSchoolCompanyPartnershipMap,
    AppWidgetCard,
} from "../../../components";
import { PartnershipCompanyForm as FormComponent } from "./PartnershipCompanyForm";
import { PartnershipCompanyReferenceTrainerForm as FormComponent2 } from "./PartnershipCompanyReferenceTrainerForm";
import { PartnershipCompanyFilter as FilterComponent } from "./PartnershipCompanyFilter";
import {
    Company as Entity,
    CompanyApi as EntityApi,
    Partnership,
    PartnershipApi,
} from "../../../apis";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC, cPath, UTIL } from "../../../config";

export const PartnershipCompanyPage = () => {
    // hook
    const { t } = useTrans();
    const location = useLocation();
    const { user, grant } = useAuthData();
    const toast = useToast();
    const formContainerState = useFormContainerState();
    const navigate = useNavigate();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);
    const [searchParams] = useSearchParams();

    // state & const
    const searchValue = searchParams.get("q");
    const isCreate =
        window.location.pathname ===
        cPath("ADMIN.CREATE_PARTNERSHIP_COMPANY_PAGE_REQUEST", {});
    const isRequest =
        window.location.pathname ===
        cPath("ADMIN.REQUEST_PARTNERSHIP_COMPANY_PAGE", {});
    const isPartnership = !isCreate && !isRequest;

    const [search, setSearch] = useState("");
    const [localSearch, setLocalSearch] = useState("");
    const [filterData, setFilterData] = useState<any>({});
    const [loadingList, setLoadingList] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const [data, setData] = useState<Entity>();
    const cancelTokenSourcesRef = useRef<Canceler[]>([]);
    const [page, setPage] = useState(1);
    const [pagesCount, setPagesCount] = useState(0);
    const [partnershipStatusList, setPartnershipStatusList] = useState<
        Record<string, Partnership>
    >({});
    const [partnershipStatus, setPartnershipStatus] = useState<Partnership>();
    const [viewType, setViewType] = useState<
        (typeof APPC.FE.ViewType)[keyof typeof APPC.FE.ViewType]
    >(
        isCreate && !searchValue
            ? APPC.FE.ViewType.VIEW_TYPE_MAP
            : APPC.FE.ViewType.VIEW_TYPE_LIST
    );

    const [mapCenter, setMapCenter] = useState<[number, number]>([
        48.8575475, 2.3513765,
    ]);
    const pageSize = APPC.PAGE_SIZES[1];
    const PARTNERSHIPSTATUS = APPC.BE.Partnership.STATUS;
    const VIEWTYPE = APPC.FE.ViewType;
    const dayjs = UTIL.getDayJs();

    // handler
    const fetchListData = () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            "order[name]": "asc",
        };

        if (isCreate) {
            if (viewType === VIEWTYPE.VIEW_TYPE_MAP) {
                Object.assign(params, {
                    xLatitude: mapCenter[0],
                    xLongitude: mapCenter[1],
                    xRadius: 10000,
                    pagination: false,
                });
            }
            Object.assign(params, filterData);
            if (search) {
                params["company_search"] = search;
            }
            if (searchValue) {
                params["name"] = searchValue;
            }
            if (localSearch !== search) {
                setLocalSearch(search);
                if (page != 1) {
                    setPage(1);
                    return;
                }
            }
            const method =
                viewType === VIEWTYPE.VIEW_TYPE_MAP
                    ? "getCollectionFindCompaniesForMap"
                    : "getCollectionFindCompanies";
            EntityApi[method]<Entity>(page, params, (c) => {
                cancelTokenSourcesRef.current.push(c);
            })
                .then(({ errorMessage, response }) => {
                    if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (response !== null) {
                        setPagesCount(
                            Math.ceil(response.totalItems / pageSize)
                        );
                        setList(response.items);
                    }
                })
                .finally(() => setLoadingList(false));
        } else {
            params["partnerships.school.id"] =
                activeSchoolOrCompany?.["@id"] ?? "";

            if (isRequest) {
                params["partnerships.status"] = [
                    PARTNERSHIPSTATUS.STATUS_PENDING,
                    PARTNERSHIPSTATUS.STATUS_REJECTED,
                ];
            } else {
                params["partnerships.status"] =
                    PARTNERSHIPSTATUS.STATUS_APPROVED;
            }

            Object.assign(params, filterData);
            if (search) {
                params["company_search"] = search;
            }
            if (searchValue) {
                params["name"] = searchValue;
            }
            if (localSearch !== search) {
                setLocalSearch(search);
                if (page != 1) {
                    setPage(1);
                    return;
                }
            }

            EntityApi.getCollectionMyPartnership<Entity>(page, params, (c) => {
                cancelTokenSourcesRef.current.push(c);
            })
                .then(({ errorMessage, response }) => {
                    if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (response !== null) {
                        setPagesCount(
                            Math.ceil(response.totalItems / pageSize)
                        );
                        setList(response.items);
                    }
                })
                .finally(() => setLoadingList(false));
        }
    };

    const fetchPartnershipStatus = () => {
        const params = {
            pagination: false,
            ["school.id"]: activeSchoolOrCompany?.["@id"] ?? "",
        };

        PartnershipApi.getCollectionMyPartnershipsStatus<Partnership>(
            1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        ).then(({ errorMessage, response }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                const transformedStatus: Record<string, Partnership> = {};
                response.items.forEach((item) => {
                    const companyId =
                        String(item?.company?.["@id"]).split("/").pop() || "";
                    transformedStatus[companyId] = item;
                });
                setPartnershipStatusList(transformedStatus);
            }
        });
    };

    const actionOptions: AppOptionBoxItem[] = [
        {
            key: "referenceTrainer",
            label: t(
                "padm.PartnershipCompanyPage:text.option.referenceTrainer"
            ),
            iconName: "icl-chart-pie",
            isDisabled: grant.isSchoolDirector,
        },
    ];

    const onActionClick = (key: string, data?: Entity) => {
        if (!data) {
            return;
        }
        switch (key) {
            case "referenceTrainer":
                setPartnershipStatus(partnershipStatusList[data.id]);
                formContainerState.open(data?.id);
                break;
        }
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.isOpen) {
            fetchListData();
        }
    }, [
        page,
        search,
        filterData,
        isCreate,
        isRequest,
        activeSchoolOrCompany,
        formContainerState.isOpen,
        viewType,
        mapCenter,
    ]);

    useEffect(() => {
        if (!formContainerState.isOpen) {
            fetchPartnershipStatus();
        }
    }, [activeSchoolOrCompany, isRequest, formContainerState.isOpen, page]);

    useEffect(() => {
        if (page != 1) {
            setPage(1);
        }
    }, [filterData, viewType]);

    useEffect(() => {
        if (page != 1) {
            setPage(1);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (isCreate && !searchValue) {
            setViewType(VIEWTYPE.VIEW_TYPE_MAP);
        } else {
            setViewType(VIEWTYPE.VIEW_TYPE_LIST);
        }
    }, [isCreate]);

    useEffect(() => {
        const lat = Number(activeSchoolOrCompany?.latitude);
        const lng = Number(activeSchoolOrCompany?.longitude);

        if (lat && lng) {
            setMapCenter([lat, lng]);
        }
    }, [activeSchoolOrCompany]);

    const getTitle = () => {
        return isCreate
            ? t("padm.PartnershipCompanyPage:text.create")
            : isRequest
              ? t(
                    "padm.PartnershipCompanyPage:text.partnershipCompanyRequestPageTitle"
                )
              : t(
                    "padm.PartnershipCompanyPage:text.partnershipCompanyPageTitle"
                );
    };

    return (
        <>
            <AppPageHeader
                title={getTitle()}
                breadcrumb={
                    isCreate
                        ? [
                              {
                                  label: t(
                                      "padm.PartnershipCompanyPage:text.partnershipCompanyRequestPageTitle"
                                  ),
                                  url: cPath(
                                      "ADMIN.REQUEST_PARTNERSHIP_COMPANY_PAGE"
                                  ),
                              },
                          ]
                        : []
                }
            >
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    {isCreate && (
                        <>
                            <ButtonGroup size="sm" isAttached variant="outline">
                                <Button
                                    className="btn-icon-md"
                                    size="md"
                                    isActive={
                                        viewType === VIEWTYPE.VIEW_TYPE_MAP
                                    }
                                    onClick={() =>
                                        setViewType(VIEWTYPE.VIEW_TYPE_MAP)
                                    }
                                >
                                    <AppIcon
                                        name="icl-location-pin"
                                        w="1.25rem"
                                    />
                                </Button>
                                <Button
                                    className="btn-icon-md"
                                    size="md"
                                    isActive={
                                        viewType === VIEWTYPE.VIEW_TYPE_LIST
                                    }
                                    onClick={() =>
                                        setViewType(VIEWTYPE.VIEW_TYPE_LIST)
                                    }
                                >
                                    <AppIcon name="icl-list" w="1.25rem" />
                                </Button>
                            </ButtonGroup>
                            <AppSearch onSearch={(value) => setSearch(value)} />
                            <FilterComponent
                                filterData={filterData}
                                setFilterData={setFilterData}
                            >
                                <Box role="button">
                                    <AppFilterButton filterData={filterData} />
                                </Box>
                            </FilterComponent>
                        </>
                    )}
                    {isRequest &&
                        (user?.userType ===
                            APPC.BE.User.USERTYPE
                                .USERTYPE_INTERNSHIP_COORDINATOR_TRAINER ||
                            user?.userType ===
                                APPC.BE.User.USERTYPE
                                    .USERTYPE_SCHOOL_AND_COMPANY) && (
                            <Button
                                variant={"primary"}
                                onClick={() => {
                                    setViewType(VIEWTYPE.VIEW_TYPE_MAP);
                                    navigate(
                                        cPath(
                                            "ADMIN.CREATE_PARTNERSHIP_COMPANY_PAGE_REQUEST",
                                            {}
                                        )
                                    );
                                }}
                            >
                                <AppIcon name="ics-plus-circle" w="1rem" />
                                <Hide below="md">
                                    <Text ml={2}>
                                        {t(
                                            "padm.PartnershipCompanyPage:button.create"
                                        )}
                                    </Text>
                                </Hide>
                            </Button>
                        )}
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <>
                    {!isCreate && (
                        <HStack
                            px={{ base: 3, sm: 6 }}
                            pt={{ base: 3.5, sm: 4, md: "1.125rem" }}
                            gap={0}
                        >
                            <Button
                                size={"sm"}
                                variant="tab"
                                isActive={isPartnership}
                                onClick={() =>
                                    navigate(
                                        cPath("ADMIN.PARTNERSHIP_COMPANY_PAGE")
                                    )
                                }
                            >
                                <Hide below="sm">
                                    <Text ml={2}>
                                        {t(
                                            "padm.PartnershipCompanyPage:text.partnershipCompanyPageTitle"
                                        )}
                                    </Text>
                                </Hide>
                            </Button>
                            <Button
                                size={"sm"}
                                variant="tab"
                                isActive={isRequest}
                                onClick={() =>
                                    navigate(
                                        cPath(
                                            "ADMIN.REQUEST_PARTNERSHIP_COMPANY_PAGE"
                                        )
                                    )
                                }
                            >
                                <Hide below="sm">
                                    <Text ml={2}>
                                        {t(
                                            "padm.PartnershipCompanyPage:text.partnershipCompanyRequestPageTitle"
                                        )}
                                    </Text>
                                </Hide>
                            </Button>
                        </HStack>
                    )}
                    {viewType === VIEWTYPE.VIEW_TYPE_LIST && (
                        <>
                            {loadingList ? (
                                <AppLoader />
                            ) : (
                                <VStack p={6} gap={6} alignItems={"stretch"}>
                                    {list.map((item) => (
                                        <AppWidgetCard
                                            key={item.id}
                                            data={item}
                                            partnership={
                                                !isCreate && !isRequest
                                                    ? partnershipStatusList[
                                                          item?.id
                                                      ]
                                                    : undefined
                                            }
                                            actionElement={
                                                isCreate ? (
                                                    <Button
                                                        size={"sm"}
                                                        onClick={() => {
                                                            formContainerState.open(
                                                                item?.id
                                                            );
                                                            setData(item);
                                                        }}
                                                        isDisabled={
                                                            !!partnershipStatusList[
                                                                item?.id
                                                            ]
                                                        }
                                                    >
                                                        {t(
                                                            "padm.PartnershipCompanyPage:button.create"
                                                        )}
                                                    </Button>
                                                ) : isRequest ? (
                                                    <>
                                                        {Object.keys(
                                                            partnershipStatusList
                                                        ).includes(
                                                            String(item?.id)
                                                        ) &&
                                                        partnershipStatusList[
                                                            item?.id
                                                        ]?.status !==
                                                            PARTNERSHIPSTATUS.STATUS_APPROVED ? (
                                                            <VStack
                                                                alignItems={{
                                                                    base: "flex-start",
                                                                    sm: "flex-start",
                                                                    md: "flex-end",
                                                                }}
                                                                gap={1.5}
                                                            >
                                                                <Text
                                                                    textStyle={
                                                                        "regularXs"
                                                                    }
                                                                >
                                                                    {`${t(
                                                                        "padm.PartnershipCompanyPage:text.requestSentAt"
                                                                    )} ${dayjs(
                                                                        partnershipStatusList[
                                                                            item
                                                                                ?.id
                                                                        ]
                                                                            ?.createdAt
                                                                    ).format(
                                                                        APPC.APP_DATEANDTIME_FORMAT
                                                                    )}`}
                                                                </Text>
                                                                <Badge
                                                                    variant={
                                                                        partnershipStatusList[
                                                                            item
                                                                                ?.id
                                                                        ]
                                                                            ?.status ===
                                                                        PARTNERSHIPSTATUS.STATUS_PENDING
                                                                            ? "green"
                                                                            : "red"
                                                                    }
                                                                >
                                                                    {t(
                                                                        `con.Partnership:STATUS_${
                                                                            partnershipStatusList[
                                                                                item
                                                                                    ?.id
                                                                            ]
                                                                                ?.status
                                                                        }`
                                                                    )}
                                                                </Badge>
                                                            </VStack>
                                                        ) : (
                                                            <></>
                                                        )}
                                                    </>
                                                ) : (
                                                    <AppOptionsBox
                                                        options={actionOptions}
                                                        data={item}
                                                        onClick={onActionClick}
                                                    />
                                                )
                                            }
                                        />
                                    ))}
                                    {pagesCount > 1 && list.length > 0 && (
                                        <AppPagination
                                            onClick={(pageNumber) =>
                                                setPage(pageNumber)
                                            }
                                            totalPages={pagesCount}
                                            activePage={page}
                                        />
                                    )}
                                </VStack>
                            )}
                        </>
                    )}
                    {isCreate && viewType === VIEWTYPE.VIEW_TYPE_MAP && (
                        <VStack
                            p={6}
                            gap={6}
                            alignItems="stretch"
                            justifyContent="stretch"
                            h={"100%"}
                            minH={"400px"}
                        >
                            <AppSchoolCompanyPartnershipMap
                                list={list}
                                partnershipStatusList={partnershipStatusList}
                                activeSchoolOrCompany={activeSchoolOrCompany}
                                actionElement={(company: Entity) => (
                                    <Button
                                        size="xs"
                                        onClick={() => {
                                            formContainerState.open(company.id);
                                            setData(company);
                                        }}
                                    >
                                        {t(
                                            "padm.PartnershipCompanyPage:button.create"
                                        )}
                                    </Button>
                                )}
                                onCenterChange={(lat, lng) => {
                                    setMapCenter((prev) => {
                                        if (
                                            Math.abs(prev[0] - lat) < 0.002 &&
                                            Math.abs(prev[1] - lng) < 0.002
                                        ) {
                                            return prev;
                                        }
                                        return [lat, lng];
                                    });
                                }}
                                mapProps={{ center: mapCenter }}
                            />
                        </VStack>
                    )}
                    {formContainerState.isOpen && isCreate && (
                        <FormComponent
                            formContainerState={formContainerState}
                            data={data}
                        />
                    )}
                    {formContainerState.isOpen &&
                        !isCreate &&
                        !isRequest &&
                        partnershipStatus && (
                            <FormComponent2
                                formContainerState={formContainerState}
                                data={partnershipStatus}
                            />
                        )}
                </>
            </AppPageContent>
        </>
    );
};
